import { getBlob } from "../Services/ApiBase";
import { API_ENDPOINTS } from "../Services/ApiEndPoints";
import { getApiErrorMessage, isSilentRequestAbortError } from "./apiErrorMessage";

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return null;
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return null;
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1].trim();
  const plain = /filename=([^;\s]+)/i.exec(header);
  return plain ? plain[1].trim().replace(/^["']|["']$/g, "") : null;
}

async function blobToErrorMessage(blob) {
  try {
    const text = await blob.text();
    const j = JSON.parse(text);
    return j.message || j.error || j.data?.message || text || "Export failed";
  } catch {
    return "Export failed";
  }
}

function omitEmptyParams(params) {
  const out = {};
  if (!params || typeof params !== "object") return out;
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
}

/**
 * GET /audit-logs/export — downloads the month-lock audit log as CSV or XLSX.
 * @param {object} params - filter params (user_id, action, date_from, date_to, month_lock_id)
 * @param {'csv' | 'xlsx'} format
 */
export async function downloadAuditLogExport(params = {}, format = "csv") {
  const fmt = format === "xlsx" ? "xlsx" : "csv";
  const today = new Date().toISOString().slice(0, 10);
  const fallbackFilename = `audit-log-${today}.${fmt}`;
  const query = omitEmptyParams({ ...params, format: fmt });

  try {
    const response = await getBlob(API_ENDPOINTS.REPORTS.AUDIT_LOGS_EXPORT, query);
    const blob = response.data;
    const contentType = String(
      response.headers?.["content-type"] || response.headers?.["Content-Type"] || ""
    );

    if (contentType.includes("application/json")) {
      throw new Error(await blobToErrorMessage(blob));
    }

    const cd =
      response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];
    const filename = parseFilenameFromContentDisposition(cd) || fallbackFilename;

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (err) {
    if (isSilentRequestAbortError(err)) throw err;
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const msg = await blobToErrorMessage(data);
      throw new Error(msg, { cause: err });
    }
    throw new Error(getApiErrorMessage(err, "Export failed"), { cause: err });
  }
}
